<template>
<a :href="`/broadcast/${broadcast.id}`" :class="`broadcast ${compact ? 'compact' : ''}`" @click.prevent="handleLink($event, `/broadcast/${broadcast.id}`)">
  <td :class="`${compact ? 'w-66p' : 'f-2'}`">
    <div class="info">
      <div class="image-wrapper">
        <div class="image" :style="`${broadcast.images && broadcast.images.length > 0 ? `background-image: url(https://${broadcast.fqdn}/images/h/160/${broadcast.images[0]})` : ''}`"></div>
      </div>
      <div class="desc">
        <h3>{{ broadcast.metadata.title }}</h3>
        <div class="functions">
          <template v-if="isLive">
            <pill class="m-r-1 live">{{ liveStatus }}</pill>
          </template>
          <template v-if="!['complete', 'abandoned'].includes(broadcast.status.life_cycle_status) && broadcast.stream.status === 'active'">
            <div :class="`stream ${['liveStarting', 'live', 'liveStopping'].includes(broadcast.status.life_cycle_status) && broadcast.stream.status !== 'active' ? 'red' : ''} ${broadcast.stream.status === 'active' ? 'green' : ''}`">
              <i :class="`icon-broadcast`" />
              <span>
                <template v-if="['liveStarting', 'live', 'liveStopping'].includes(broadcast.status.life_cycle_status) && broadcast.stream.status !== 'active'">
                  Not receiving stream
                </template>
                <template v-else-if="broadcast.stream.status === 'active'">
                  Receiving stream
                </template>
                <template v-else>
                  Inactive stream
                </template>
              </span>
            </div>
          </template>
          <template v-if="showType && !isLive">
            <pill :class="`m-r-1 white-text ${broadcast.status.life_cycle_status}`">
              <template v-if="broadcast.status.life_cycle_status === 'complete'">
                <i class="icon-checkbox-thin" />
                Complete
              </template>
              <template v-else-if="['ready', 'created'].includes(broadcast.status.life_cycle_status)">
                <i class="icon-event" />
                Scheduled
              </template>
              <template v-else-if="['abandoned', 'error'].includes(broadcast.status.life_cycle_status)">
                <i class="icon-close-x" />
                {{ broadcast.status.life_cycle_status}}
              </template>
            </pill>
          </template>
          <span :title="broadcast.scheduled_start_time | formattime(false)" class="function start-time">
            <i class="icon-clock-fill" />
            <span>{{ broadcast.scheduled_start_time | formattime }}</span>
          </span>
          <template v-if="broadcast.status.life_cycle_status !== 'abandoned'">
            <span class="function active" v-if="broadcast.event_details.chat.enable">
              <i class="icon-checkbox-thin" />
              <span>Chat</span>
            </span>
            <span class="function active" v-if="broadcast.event_details.questions.enable">
              <i class="icon-checkbox-thin" />
              <span>QA</span>
            </span>
            <span class="function active" v-if="broadcast.event_details.radio.enable && featuresEnabled.includes('ppv')">
              <i class="icon-checkbox-thin" />
              <span>Radio</span>
            </span>
            <span class="function active" v-if="broadcast.event_details.scorebar.enable && featuresEnabled.includes('scorebar')">
              <i class="icon-checkbox-thin" />
              <span>Scorebar</span>
            </span>
            <span class="function active" v-if="broadcast.event_details.multi_camera.enable && featuresEnabled.includes('ppv')">
              <i class="icon-checkbox-thin" />
              <span>Multiple cameras</span>
            </span>
          </template>
        </div>
      </div>
    </div>
    <i class="arrow mdi mdi-chevron-right"></i>
  </td>
  <td v-if="compact" class="start-time w-33 ta-c" :title="(['live', 'liveStopping', 'complete'].includes(broadcast.status.life_cycle_status) ? broadcast.actual_start_time : broadcast.scheduled_start_time) | formattime(false)">
    <template v-if="['live', 'liveStopping', 'complete'].includes(broadcast.status.life_cycle_status)">
      <span class="function">
        <i class="icon-clock-fill" />
        <span>
        <template v-if="broadcast.actual_start_time.includes('0001-01-01')">
          {{ broadcast.scheduled_start_time | formattime }}
        </template>
        <template v-else>
          {{ broadcast.actual_start_time | formattime }}
        </template>
        </span>
      </span>
    </template>
    <template v-else>
      <span class="function">
        <i class="icon-clock-fill" />
        <span>
        {{ broadcast.scheduled_start_time | formattime }}
        </span>
      </span>
    </template>
    <i class="arrow mdi mdi-chevron-right"></i>
  </td>
</a>
</template>

<script>

import moment from 'moment';

import Pill from '@/components/Pill.vue';

export default {
  components: {
    Pill,
  },
  props: {
    broadcast: {
      type: Object,
      required: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    showType: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    liveStatus() {
      switch (this.broadcast.status.life_cycle_status) {
        case 'liveStarting':
          return 'Starting';
        case 'live':
          return 'Live';
        case 'liveStopping':
          return 'Stopping';
        default:
          return '';
      }
    },
    /**
     * Return boolean if the broadcast is live
     * @returns {Boolean}
     */
    isLive() {
      return ['liveStarting', 'live', 'liveStopping'].indexOf(this.broadcast.status.life_cycle_status) >= 0;
    },
    /**
     * Within 24 hours
     * Returns a boolean wether or not the broadcast is to start in the next 24h
     * @return {Bool}
     */
    within24hours() {
      if (['complete', 'abandoned'].indexOf(this.broadcast.status.life_cycle_status)) {
        return moment().unix() - moment(this.broadcast.actual_start_time).unix() <= 86400;
      }
      return moment(this.broadcast.scheduled_start_time).unix() - moment().unix() <= 86400;
    },
  },
  filters: {
    formattime: (date, humanize = true) => (humanize ? moment(date).fromNow() : moment(date).format('YYYY-MM-DD HH:mm')),
  },
};
</script>

<style lang="scss" scoped>
.broadcast {
  display: table-row;
  position: relative;
  border-radius: var(--card-border-radius);
  vertical-align: middle;

  &:hover {
    background-color: red;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #EEF0F9;
  }

  & > div {
    padding: 12px;
    &:last-of-type {
      padding-right: 50px;
    }
  }

  &:not(.compact) {
    td.start-time {
      display: none;
    }
  }

  .pills {
    margin-top: 4px;
    width: 100%;

    .pill {
      margin-right: 4px;
    }
  }

  .pill {
    // margin-bottom: 2px;
  }

  .info {
    align-items: center;
    display: flex;

    .image-wrapper {
      .image {
        background-color: #000000;
        background-position: 50% 50%;
        background-size: contain;
        background-repeat: no-repeat;
        height: 45px;
        width: 80px;
        border-radius: 6px;
        transition: height .3s, width .3s;
      }

      /*
      img {
        border-radius: 6px;
        min-width: 106px;
        max-width: 106px;
        transition: all .3s;
        width: 100%;
      }
      .no-image {
        background-color: #E6E9ED;
        border-radius: 6px;
        height: 60px;
        width: 106px;
        transition: height .3s, width .3s;
      }
      */
    }
    .desc {
      padding: 0 50px 0 10px;
      overflow: hidden;
      flex: 1;

      h3 {
        color: var(--color-font-primary);
        font-size: 16px;
        font-weight: 500;
        line-height: 22px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      .functions {
        display: flex;
        padding: 0;
        margin-top: 3px;
        flex-wrap: wrap;
        align-items: center;
      }
    }
  }

  .stream {
    align-items: center;
    display: inline-flex;
    color: #868B97;
    font-size: 16px;
    margin-left: 0;
    margin-right: 5px;

    i {
      margin-right: 3px;
    }

    &.red {
      i {
        color: var(--color-cta-red);
      }
    }
    &.green {
      i {
        color: var(--color-cta-green);
      }
    }
  }

  .function {
    color: var(--color-cta-gray);
    position: relative;
    padding-left: 16px;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;

    &.active {
      i {
        color: var(--color-cta-green);
      }
    }

    i {
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      font-size: 14px;
      line-height: 0;
    }

    span {
      font-size: 12px;
      text-transform: capitalize;
      line-height: 18px;
    }

    &:not(:last-child) {
      margin-right: 5px;
    }
  }
  /*
  & > .start-time {
    align-items: center;
    display: none;
    justify-content: center;
    text-align: center;
    white-space: nowrap;
  }
  */

  .arrow {
    color: var(--color-cta-blue);
    position: absolute;
    right: 10px;
    top: 50%;
    transition: all .3s;
    transform: translateY(-50%);
    font-size: 30px;
  }

  &:not(:last-child) {
    margin-bottom: 12px;
  }

  &.compact {
    background-color: unset;
    border-radius: 0;
    box-shadow: unset;
    margin-bottom: 0;

    &:not(:first-child) {
      border-top: 1px solid var(--color-card-inside-border);
    }

    .info {
      .image-wrapper {
        .image {
          height: 37px;
          width: 66px;
        }
      }

      .desc {
        padding: 0 10px;

        h3 {
          font-size: 14px;
          line-height: 20px;
        }
      }

      .start-time {
        display: none;
      }
    }

    & > .start-time {
    }
  }

  @media (min-width: 768px) {
    .arrow {
      right: 20px;
      font-size: 40px;
    }

    .info {
      .desc {
        .functions {
          margin-top: 6px;
        }
      }
    }

    .function {
      padding-left: 20px;

      i {
        font-size: 18px;
      }

      span {
        font-size: 16px;
      }

      &:not(:last-child) {
        margin-right: 10px;
      }
    }

    &:not(.compact) {
      .info {
        .image-wrapper {
          .image {
            height: 72px;
            width: 128px;
          }
        }

        .desc {
          padding: 0 50px 0 20px;
          h3 {
            font-size: 20px;
            line-height: 26px;
          }
        }
      }
    }

    &.compact {
      .info {
        .image-wrapper {
          .image {
            width: 96px;
            height: 54px;
          }
        }

        .desc {
          padding: 0 10px;

          h3 {
            font-size: 16px;
            line-height: 20px;
          }
        }
      }
    }
  }
}
</style>
